/* eslint-disable unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars */
import clsx from 'clsx'
import React, { useState, type FC } from 'react'

import { Text } from '@wartek-id/text-v1'
import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'

import { TableRow, TableRowItem } from 'app/KinerjaShared/components/Table'
import BadgeStatusPengiriman from 'app/KinerjaShared/BadgeStatusPengiriman'
import DialogPredikatKinerjaTahunanUnavailable from 'app/KinerjaDinasPage/components/DialogPredikatKinerjaTahunanUnavailable'

import type { DialogPredikatKinerjaTahunanUnavailableProps } from 'app/KinerjaDinasPage/components/DialogPredikatKinerjaTahunanUnavailable'
import type {
  TPenilaianTahunan,
  TPenilaianTahunanStatePengiriman,
} from 'types/kinerja-dinas-penilaian'

type RowPredikatKinerjaPenetapanTahunanProps = {
  items: TPenilaianTahunan[]
  onSelect?: (item: TPenilaianTahunan) => void
}

type Data = {
  period1Filled: boolean
  period1Empty: boolean
  period2Filled: boolean
  period2Empty: boolean
  newKs: boolean
  statePengiriman: TPenilaianTahunanStatePengiriman
}

type DataResponse = [
  boolean,
  DialogPredikatKinerjaTahunanUnavailableProps['data']
]

const RowPredikatKinerjaPenetapanTahunan: FC<
  RowPredikatKinerjaPenetapanTahunanProps
> = ({ items, onSelect }) => {
  const [dialogOpen, setDialogOpen] = useState<boolean>(false)

  return (
    <>
      {items.map((item, index) => {
        const period1Filled = !!item?.predikatKinerjaPeriode1
        const period1Empty = !period1Filled

        const period2Filled = !!item?.predikatKinerjaPeriode2
        const period2Empty = !period2Filled

        const data: Data = {
          period1Filled,
          period1Empty,
          period2Filled,
          period2Empty,
          newKs: item.newKs,
          statePengiriman: item.statePengiriman,
        }

        const useRecommendation =
          item?.predikatKinerjaRekomendasi === item?.predikatKinerjaDinas ||
          !item?.predikatKinerjaDinas

        // Define whether the final predicate can be modified and eligible for sending
        const eligible = item?.statePengiriman !== 'BELUM_DINILAI'

        // Define whether the final predicate has been sent
        const sent = item?.statePengiriman === 'SUDAH_DIKIRIM'

        // Define whether the final predicate can be modified
        const editable = eligible && !sent

        return (
          <TableRow key={index}>
            <CellName item={item} />
            <CellPredicatePeriod1 item={item} data={data} />
            <CellPredicatePeriod2 item={item} data={data} />

            <CellPredicateYearRecommendation
              item={item}
              used={useRecommendation}
            />
            <CellPredicateYear item={item} overriden={!useRecommendation} />
            <CellDeliveryState item={item} />
            <CellAction
              eligible={eligible}
              editable={editable}
              onClick={() => {
                onSelect(item)
              }}
            />
          </TableRow>
        )
      })}
    </>
  )
}

const getDataPeriod1 = ({
  period1Filled,
  period1Empty,
  period2Filled,
  period2Empty,
  newKs,
  statePengiriman,
}: Data): DataResponse => {
  // Case #2, #6, #7, #8
  // Do nothing, as it's happy path

  // Case #3
  if (
    period1Empty &&
    period2Filled &&
    !newKs &&
    statePengiriman === 'BELUM_DIKIRIM'
  ) {
    return [
      true,
      {
        title: 'Predikat Kinerja (Tahunan) tetap dapat dikirim',
        description:
          'Data tidak tersedia karena data berasal dari satdik sebelumnya.',
        recommendation:
          'Rekomendasi Predikat Kinerja (Tahunan) otomatis berdasarkan data Predikat Kinerja yang tersedia.',
      },
    ]
  }

  // Case #4, #5
  else if (
    period1Empty &&
    period2Filled &&
    newKs &&
    statePengiriman === 'BELUM_DIKIRIM'
  ) {
    return [
      true,
      {
        title: 'Predikat Kinerja (Tahunan) tetap dapat dikirim',
        description:
          'Data tidak tersedia karena Pegawai ini baru memperoleh Penugasan JF Guru di periode Juli-Desember.',
        recommendation:
          'Rekomendasi Predikat Kinerja (Tahunan) otomatis berdasarkan data Predikat Kinerja yang tersedia.',
      },
    ]
  }

  // Case #9, #11
  else if (
    period1Empty &&
    period2Filled &&
    !newKs &&
    statePengiriman === 'BELUM_DINILAI'
  ) {
    return [
      true,
      {
        title: 'Predikat Kinerja (Tahunan) tidak dapat dikirim',
        description:
          'Periode Januari-Juni untuk Pegawai ini belum selesai. Diskusi dengan Pegawai untuk info selengkapnya.',
      },
    ]
  }

  return [false, null]
}

const getDataPeriod2 = ({
  period1Filled,
  period1Empty,
  period2Filled,
  period2Empty,
  newKs,
  statePengiriman,
}: Data): DataResponse => {
  // Case #10
  if (
    period1Filled &&
    period2Empty &&
    !newKs &&
    statePengiriman === 'BELUM_DINILAI'
  ) {
    return [
      true,
      {
        title: 'Predikat Kinerja (Tahunan) tidak dapat dikirim',
        description:
          'Periode Juli-Desember untuk Pegawai ini belum selesai. Diskusi dengan Pegawai untuk info selengkapnya.',
      },
    ]
  }

  // Case #12
  // To be confirmed

  // Case #13
  // To be confirmed

  return [false, null]
}

const CellName = ({ item }: { item: TPenilaianTahunan }) => {
  return (
    <TableRowItem className="align-middle space-y-1">
      <Text
        variant="body-sm"
        color="default"
        className="overflow-wrap-anywhere !font-bold"
      >
        {item?.name || ''}
      </Text>
      <Text variant="helper" color="default" className="overflow-wrap-anywhere">
        {item?.jabatan || ''}
      </Text>
      <Text variant="helper" color="default" className="overflow-wrap-anywhere">
        {item?.satuanPendidikan || ''}
      </Text>
    </TableRowItem>
  )
}

const CellPredicatePeriod1 = ({
  item,
  data,
}: {
  item: TPenilaianTahunan
  data: Data
}) => {
  const hasValue = !!item?.predikatKinerjaPeriode1
  const [dialogOpen, setDialogOpen] = useState<boolean>(false)
  const [requireDialog, dialogData] = getDataPeriod1({ ...data })

  return (
    <TableRowItem className="align-middle pl-0">
      <div className="flex items-center gap-2.5">
        <Text variant="subheading" className="overflow-wrap-anywhere uppercase">
          {hasValue && item?.predikatKinerjaPeriode1}
          {!hasValue && '-'}
        </Text>

        {requireDialog && (
          <>
            <Button
              iconOnly
              size="sm"
              className="!bg-transparent"
              aria-label="Lihat Info"
              onClick={() => setDialogOpen(true)}
            >
              <Icon>help</Icon>
            </Button>
          </>
        )}
      </div>

      {requireDialog && (
        <DialogPredikatKinerjaTahunanUnavailable
          data={dialogData}
          isOpen={dialogOpen}
          onClose={() => setDialogOpen(false)}
        />
      )}
    </TableRowItem>
  )
}

const CellPredicatePeriod2 = ({
  item,
  data,
}: {
  item: TPenilaianTahunan
  data: Data
}) => {
  const hasValue = !!item?.predikatKinerjaPeriode2
  const [dialogOpen, setDialogOpen] = useState<boolean>(false)
  const [requireDialog, dialogData] = getDataPeriod2({ ...data })

  return (
    <TableRowItem className="align-middle pl-0">
      <div className="flex items-center gap-2.5">
        <Text variant="subheading" className="overflow-wrap-anywhere uppercase">
          {hasValue && item?.predikatKinerjaPeriode2}
          {!hasValue && '-'}
        </Text>

        {requireDialog && (
          <>
            <Button
              iconOnly
              size="sm"
              className="!bg-transparent"
              aria-label="Lihat Info"
              onClick={() => setDialogOpen(true)}
            >
              <Icon>help</Icon>
            </Button>
          </>
        )}
      </div>

      {requireDialog && (
        <DialogPredikatKinerjaTahunanUnavailable
          data={dialogData}
          isOpen={dialogOpen}
          onClose={() => setDialogOpen(false)}
        />
      )}
    </TableRowItem>
  )
}

const CellPredicateYearRecommendation = ({
  item,
  used,
}: {
  item: TPenilaianTahunan
  used: boolean
}) => {
  return (
    <TableRowItem className="align-middle pl-0">
      <Text
        variant="subheading"
        color={used ? 'informational' : 'subdued'}
        className={clsx(
          'overflow-wrap-anywhere uppercase',
          used && '!text-blue-60'
        )}
      >
        {item?.predikatKinerjaRekomendasi || '-'}
      </Text>
    </TableRowItem>
  )
}

const CellPredicateYear = ({
  item,
  overriden,
}: {
  item: TPenilaianTahunan
  overriden: boolean
}) => {
  const finalPredicate = overriden ? item?.predikatKinerjaDinas || '-' : '-'

  return (
    <TableRowItem className="align-middle pl-0">
      <Text
        variant="subheading"
        color={overriden ? 'informational' : 'default'}
        className={clsx(
          'overflow-wrap-anywhere uppercase',
          overriden && '!text-blue-60'
        )}
      >
        {finalPredicate}
      </Text>
    </TableRowItem>
  )
}

const CellDeliveryState = ({ item }: { item: TPenilaianTahunan }) => {
  return (
    <TableRowItem className="align-middle pl-0">
      <BadgeStatusPengiriman<TPenilaianTahunanStatePengiriman>
        status={item.statePengiriman}
        fallback={(status) => {
          if (status === 'BELUM_DINILAI') {
            return {
              children: 'Tidak dapat dikirim',
              variant: 'critical',
              className: '!bg-gray-10',
            }
          }

          return { children: status }
        }}
      />
    </TableRowItem>
  )
}

const CellAction = ({
  eligible,
  editable,
  onClick,
}: {
  eligible: boolean
  editable: boolean
  onClick: () => void
}) => {
  return (
    <TableRowItem className="align-middle pl-0">
      <div className="flex flex-row items-center justify-center space-x-6">
        <Button
          size="sm"
          color="white"
          disabled={!editable}
          className={clsx(!editable && '!outline-0')}
          onClick={onClick}
        >
          {eligible && <>Ubah Predikat Kinerja</>}
          {!eligible && <>Predikat tidak lengkap</>}
        </Button>
      </div>
    </TableRowItem>
  )
}

export default RowPredikatKinerjaPenetapanTahunan
